import moment from 'moment-timezone';

import {
  IVehicle,
  ECoverageVariableKey,
  EVehicleOwnershipStatus,
} from '../../interfaces';

import { calculateVehicleAge } from './calculateVehicleAge';
import { getOemHiddenVehicleRegionRules } from './regionUtilities/hiddenVehicleRegionRules';

type TVehicle = Pick<IVehicle, 'garage_address' | 'in_storage' | 'year'> & {
  ownership_status: EVehicleOwnershipStatus | null;
};

export type THiddenVehicleCoverageWithReason = {
  hiddenCoverage: ECoverageVariableKey;
  reason: string;
};

type TContext = {
  baseVehicleYear?: number;
  isHideRidesharing?: boolean;
};

export const HIDDEN_VEHICLE_REASONS = {
  HIDE_RIDESHARING: 'hideRidesharing',
  NO_VEHICLE_YEAR: 'noVehicleYear',
  VEHICLE_AGE_GREATER_THAN_1_YEAR: 'vehicleAgeGreaterThanOne',
  VEHICLE_AGE_GREATER_THAN_3_YEARS: 'vehicleAgeGreaterThanThree',
  VEHICLE_AGE_INELIGBLE_FOR_OEM: 'vehicleAgeIneligibleForOem',
  VEHICLE_IN_STORAGE: 'vehicleInStorage',
  VEHICLE_OWNED: 'vehicleOwned',
};

/**
 * Determines which vehicle coverage inputs are hidden from the user when the
 * user is adding or editing a vehicle.
 * @public
 * @param vehicle The vehicle being edited or added.
 * @param context Additional context that doesn't come from the vehicle object.
 * @param [optional] shouldIncludeReason boolean that adds reason to the array of error codes which provides insight into why the coverage should be hidden
 * @returns An array of coverage variable keys if shouldIncludeReason is FALSE else returns an array of objects of hiddenCoverageKeys and their reason.
 */
export function getHiddenVehicleCoverageFields(
  vehicle: TVehicle,
  context?: TContext,
  shouldIncludeReason?: false
): ECoverageVariableKey[];
export function getHiddenVehicleCoverageFields(
  vehicle: TVehicle,
  context?: TContext,
  shouldIncludeReason?: true
): THiddenVehicleCoverageWithReason[];

export function getHiddenVehicleCoverageFields(
  vehicle: TVehicle,
  context: TContext = {},
  shouldIncludeReason = false
): unknown {
  const hiddenVehicleCoveragesWithReasons: THiddenVehicleCoverageWithReason[] =
    [];
  const currentYear = Number(moment.utc().format('YYYY'));
  const { isHideRidesharing = false, baseVehicleYear = currentYear } = context;
  const regionCode = vehicle.garage_address.region.code;
  const isOwned =
    (vehicle.ownership_status || '').toString()?.toLowerCase() === 'owned';
  const vehicleAge = calculateVehicleAge(vehicle, baseVehicleYear);

  if (!vehicle.year || vehicleAge > 1) {
    const reason = vehicle.year
      ? HIDDEN_VEHICLE_REASONS.VEHICLE_AGE_GREATER_THAN_1_YEAR
      : HIDDEN_VEHICLE_REASONS.NO_VEHICLE_YEAR;
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'new_car_replacement',
      reason,
    });
  }

  if (!vehicle.year || vehicleAge > 3 || isOwned) {
    const reason =
      (!vehicle.year && HIDDEN_VEHICLE_REASONS.NO_VEHICLE_YEAR) ||
      (isOwned
        ? HIDDEN_VEHICLE_REASONS.VEHICLE_OWNED
        : HIDDEN_VEHICLE_REASONS.VEHICLE_AGE_GREATER_THAN_3_YEARS);
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'loan_lease_payoff',
      reason,
    });
    hiddenVehicleCoveragesWithReasons.push({ hiddenCoverage: 'gap', reason });
  }

  if (isHideRidesharing) {
    const reason = HIDDEN_VEHICLE_REASONS.HIDE_RIDESHARING;
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'ridesharing',
      reason,
    });
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'side_hustle',
      reason,
    });
  }

  if (!vehicle.year || getOemHiddenVehicleRegionRules(regionCode, vehicleAge)) {
    const reason = vehicle.year
      ? HIDDEN_VEHICLE_REASONS.VEHICLE_AGE_INELIGBLE_FOR_OEM
      : HIDDEN_VEHICLE_REASONS.NO_VEHICLE_YEAR;
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'oem_parts',
      reason,
    });
  }

  if (vehicle.in_storage) {
    const reason = HIDDEN_VEHICLE_REASONS.VEHICLE_IN_STORAGE;
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'collision_deductible_waiver',
      reason,
    });
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'collision_deductible',
      reason,
    });
    hiddenVehicleCoveragesWithReasons.push({
      hiddenCoverage: 'rental_reimbursement',
      reason,
    });
  }

  if (shouldIncludeReason) {
    return hiddenVehicleCoveragesWithReasons;
  }
  return hiddenVehicleCoveragesWithReasons.map(
    ({ hiddenCoverage }) => hiddenCoverage
  );
}
